export type Scope = "everyone" | "pilot";

export const PIN_KEY = "checkride.pinned-pilot.v1";
export const SCOPE_KEY = "checkride.lens-scope.v1";
export const SCOPE_AFTER_PIN: Scope = "pilot";

const MAX_UID_LENGTH = 256;

export interface PinRestore {
  uid: string | null;
  notice: string;
}

export function restorePin(): PinRestore {
  try {
    const raw = localStorage.getItem(PIN_KEY);
    if (!raw) return { uid: null, notice: "" };
    const record = JSON.parse(raw);
    if (record.version !== 1 || typeof record.uid !== "string" || !record.uid.trim() || record.uid.length > MAX_UID_LENGTH) {
      return { uid: null, notice: "The saved pilot selection is invalid. Please choose a pilot again." };
    }
    return { uid: record.uid, notice: "" };
  } catch {
    return { uid: null, notice: "Saved pilot selection is unavailable. You can still choose a pilot for this visit." };
  }
}

export function persistPin(uid: string | null): string {
  try {
    if (uid) localStorage.setItem(PIN_KEY, JSON.stringify({ version: 1, uid }));
    else localStorage.removeItem(PIN_KEY);
    return "";
  } catch {
    return uid
      ? "Pinned for this visit only. Browser storage is unavailable; this selection will not survive refresh."
      : "Unpinned for this visit. Browser storage could not be cleared; a saved selection may return after refresh.";
  }
}

// null means nothing stored, so the caller can choose a default (a pilot pinned before scopes existed).
export function readScope(): Scope | null {
  try {
    const raw = localStorage.getItem(SCOPE_KEY);
    if (!raw) return null;
    const record = JSON.parse(raw);
    return record.version === 1 && record.scope === "pilot" ? "pilot" : "everyone";
  } catch {
    return "everyone";
  }
}

export function writeScope(scope: Scope): void {
  try {
    localStorage.setItem(SCOPE_KEY, JSON.stringify({ version: 1, scope }));
  } catch {
    // scope still holds for this visit; it just won't survive a refresh
  }
}

// The Feed already uses ?pilot= as a name search, so there the lens is the exact ?pilot_uid= filter.
function lensParamFor(path: string): "pilot" | "pilot_uid" {
  return path.split("/").filter(Boolean)[0] === "feed" ? "pilot_uid" : "pilot";
}

export function readViewUid(hash: string): string | null {
  const [path, query] = hash.replace(/^#/, "").split("?");
  if (!query) return null;
  const raw = new URLSearchParams(query).get(lensParamFor(path));
  if (!raw || !raw.trim() || raw.length > MAX_UID_LENGTH) return null;
  return raw;
}

export function withoutPilotParam(hash: string): string {
  const [path, query = ""] = hash.replace(/^#/, "").split("?");
  const params = new URLSearchParams(query);
  params.delete(lensParamFor(path));
  const rest = params.toString();
  return `#${path}${rest ? `?${rest}` : ""}`;
}

export function resolveActiveUid(input: { viewUid: string | null; pinnedUid: string | null; scope: Scope }): string | null {
  if (input.viewUid) return input.viewUid;
  if (input.scope === "pilot" && input.pinnedUid) return input.pinnedUid;
  return null;
}
